import { DateCell, RawTextCell, TooltipCell } from '@/components/Table/components/Cell'
import { createColumnHelper } from '@tanstack/react-table'
import React from 'react'

import { PriceCell } from '@/components/Table/components/Cell'
import { camelToWords, formatCurrencyAmount, numberWithCommas, formatBaseQuotationNumber } from '@/utils/stringHelper'
import {
  OfferQuotationStatus,
  OfferControlStatus,
  OfferStage,
  OfferStatus,
  Currency,
  OfferPaymentCreditTerm,
  MaintenanceSummaryStatus,
} from '../../constants'
import {
  CostBudgetTypeLabel,
  OfferBookingStatus,
  OfferQuotationDetailInstallAndSupplyUnit,
  OfferQuotationDetailMaintenanceUnit,
  PRStatus,
  GCPRStatus,
  TenderDocumentStatus,
} from '@eapproval/offer-shared'
import { SourcingDecisionWorkflowStatus } from '@/features/offerCostBudgets/constants/offerCostBudget.constant'
import { MyOfferDisplayLevel } from '@/features/tableProfiles/constants/tableProfile.constant'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'
const columnHelper = createColumnHelper()

export const myOfferTableHeaders = {
  offerCloseDetailedReason: 'Close Detailed Reason',
  customerNumber: 'Offer To',
  updatedAt: 'Last Update',
  createdAt: 'Create Date',
  customerReference: 'Customer Ref.',
  requirePhysicalSignature: 'Physical Signature',
  LoANumber: 'LoA',
  GCKCode: 'GCK',
  bookingBond: 'Bond Completed',
  bookingInsurance: 'Insurance Completed',
  bookingEPD: 'EPD Completed',
  bookingCIC: 'CIC Completed',
  bookingBondRemark: 'Bond Remark',
  bookingInsuranceRemark: 'Insurance Remark',
  bookingEPDRemark: 'EPD Remark',
  bookingCICRemark: 'CIC Remark',
}

const commercialChecklistTableColumns = [
  columnHelper.accessor('commercialCheckListPaymentCreditTerm', {
    header: () => <span>Payment Credit Term</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: Object.values(OfferPaymentCreditTerm).map((creditTerm) => {
        return {
          value: creditTerm,
          label: creditTerm,
        }
      }),
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListPaymentBillingMilestoneMaintenance', {
    header: () => <span>Payment Billing Milestone Maintenance</span>,
    cell: RawTextCell,
    meta: {
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListPaymentBillingMilestoneProject', {
    header: () => <span>Payment Billing Milestone Project</span>,
    cell: RawTextCell,
    meta: {
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListPaymentRetention', {
    header: () => <span>Payment Retention</span>,

    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'na', label: 'NA' },
        { value: '5%', label: '5%' },
        { value: '10%', label: '10%' },
        { value: '10%Upto5%OfTheContractPrice', label: '10% up to 5% of the contract price' },
        { value: 'other', label: 'other' },
      ],
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListPaymentRetentionRelease', {
    header: () => <span>Payment Retention Release</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'na', label: 'NA' },
        {
          value: 'issuanceOfPracticalCompletionCetificate',
          label: 'Issuance of Practical Completion Certificate',
        },
        {
          value: 'issuanceOfMakingGoodDefectCertificate',
          label: 'Issuance of Making Good Defect Certificate',
        },
        { value: '50OnPCCert50OnDLPCert', label: '50% on PC Cert. + 50% on DLP Cert.' },
        { value: 'other', label: 'other' },
      ],
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListDLPPeriod', {
    header: () => <span>DLP Period</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: '12', label: '12 months' },
        { value: '18', label: '18 months' },
        { value: '24', label: '24 months' },
        { value: 'other', label: 'other' },
      ],
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListDLPCommenceDate', {
    header: () => <span>DLP shall commence from the date of</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'equipmentDelivery', label: 'equipment delivery' },
        { value: 'completionOfWork', label: 'completion of work' },
        {
          value: 'isseanceOfPracticalCompletionCertificate',
          label: 'issuance of Practical Completion Certificate',
        },
        {
          value: 'isseanceOfPracticalCompletionCertificateOfMainContract',
          label: 'issuance of Practical Completion Certificate of Main Contract',
        },
        { value: 'other', label: 'other' },
      ],
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListDLPShallExpire', {
    header: () => <span>DLP Shall Expire</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'yes', label: 'yes' },
        { value: 'no', label: 'no' },
      ],
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),

  columnHelper.accessor('commercialCheckListDLPShallExpireCert', {
    header: () => <span>DLP Shall Expire Cert</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'DLPCertForTheMainContract', label: 'DLP Cert. for the Main Contract' },
        { value: 'DLPCertForTheContract', label: 'DLP Cert. for the Contract' },
        { value: 'other', label: 'other' },
      ],
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListIsSiemensStandardTerms', {
    header: () => <span>Is Siemens Standard Terms</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'noWithLegalWaiver', label: 'No (with legal waiver)' },
      ],
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListLDForDelay', {
    header: () => <span>LD For Delay ($ per day)</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListLDForDelayCap', {
    header: () => <span>LD For Delay Cap</span>,
    cell: RawTextCell,
    meta: {
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListLDForPerformance', {
    header: () => <span>LD For Performance</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'limited', label: 'LD capped at:' },
        { value: 'unlimited', label: 'Unlimited' },
      ],
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListHasPreviousAgreementBefore', {
    header: () => <span>LD For Performance Cap</span>,
    cell: RawTextCell,
    meta: {
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),

  columnHelper.accessor('commercialCheckListLDForPerformanceCap', {
    header: () => <span>Has Previous Agreement Before</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListLiabilityPerEventCap', {
    header: () => <span>Liability Per Event Cap</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListTotalLiabilityCap', {
    header: () => <span>Total Liability Cap</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'limited', label: 'Total Liability capped at ($):' },
        { value: 'limitedPercentage', label: 'Total Liability capped at (%):' },
        { value: 'unlimited', label: 'Unlimited' },
      ],
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListIsExclusiveOfIndirectLoss', {
    header: () => <span>Is Exclusive of Indirect Loss</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListIsExclusiveOfImpliedWarranty', {
    header: () => <span>Is Exclusive of Implied Warranty</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListIsIPRtoUseOnly', {
    header: () => <span>Is cRSP To Use Only</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListRequireBond', {
    header: () => <span>Require Bond</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListWarrantyToBeSubmitted', {
    header: () => <span>Warranty To Be Submitted</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListCustomerHasRightToTerminateContract', {
    header: () => <span>Customer Has Right To Terminate Contract</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListCustomerTerminationNotifyPeriod', {
    header: () => <span>Customer Termination Notify Period</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: '30', label: '30 day notice' },
        { value: '60', label: '60 day notice' },
        { value: 'other', label: 'other' },
      ],
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListEmployeeInsuranceProvider', {
    header: () => <span>Employee Insurance Provider</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'employer', label: 'Employer' },
        { value: 'mainContractor', label: 'Main Contractor' },
        { value: 'siemens', label: 'Siemens' },
      ],
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListContractorInsuranceProvider', {
    header: () => <span>Contractor Insurance Provider</span>,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: [
        { value: 'employer', label: 'Employer' },
        { value: 'mainContractor', label: 'Main Contractor' },
        { value: 'siemens', label: 'Siemens' },
      ],
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListOtherInsuranceProvider', {
    header: () => <span>Other Insurance Provider</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListOpenAccountForEPD', {
    header: () => <span>Open Account for EPD</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListDeclareInCIC', {
    header: () => <span>Declare in CIC</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListAssignmentConsentForSubContract', {
    header: () => <span>Assignment Consent for Sub Contract</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('commercialCheckListNonDisclosureAgreement', {
    header: () => <span>Non Disclosure Agreement</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
]
const legalTableColumns = [
  columnHelper.accessor('legalProjectNature', {
    header: () => <span>Legal Project Name</span>,
    cell: RawTextCell,
    meta: {
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('legalProjectCompletionDate', {
    header: () => <span>Legal Project Completion or Contract Expiry Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('legalContactFormPreviouslyReviewed', {
    header: () => <span>Legal Contact Form Previously Reviewed (Specify project, date)</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.DATE,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('legalProjectIPLicense', {
    header: () => <span>Legal Project IP License/Transfer (Reason for transfer)</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.DATE,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('legalLoAProcessLevelRequired', {
    header: () => <span>Legal LoA Process Level Required? (Specify deadline)</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.DATE,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
]

const bookingTableColumns = ({ teamAssistantOptions = [] }) => [
  columnHelper.accessor('bookingCreateDate', {
    header: () => <span>Booking Create Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('bookingStartDate', {
    header: () => <span>Booking Project Start Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('bookingEndDate', {
    header: () => <span>Booking Project End Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('bookingSubmittedDate', {
    header: () => <span>Booking Submitted Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('bookingStartedWorkflow', {
    header: () => <span>Booking Started Workflow</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('bookingTeamAssistant', {
    header: () => <span>Booking Team Assistant</span>,
    cell: RawTextCell,
    meta: {
      filterVariant: FilterType.MULTI_SELECT,
      filterOptions: teamAssistantOptions,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('bookingWBSNumber', {
    header: () => <span>Booking WBS Number</span>,
    cell: RawTextCell,
    meta: {
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('SONumber', {
    header: () => <span>Booking SO Number</span>,
    cell: RawTextCell,
    meta: {
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('bookingCustomerPONumber', {
    header: () => <span>Booking Customer PO Number</span>,
    cell: RawTextCell,
    meta: {
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('bookingCustomerPODate', {
    header: () => <span>Booking Customer PO Date</span>,
    cell: DateCell,
    meta: {
      filterVariant: FilterType.DATE,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('bookingBond', {
    header: () => <span>Bond Completed</span>,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('bookingInsurance', {
    header: () => <span>Insurance Completed</span>,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('bookingEPD', {
    header: () => <span>EPD Completed</span>,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('bookingCIC', {
    header: () => <span>CIC Completed</span>,
    meta: {
      filterVariant: FilterType.SELECT_YES_NO,
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('bookingBondRemark', {
    header: () => <span>Bond Remark</span>,
    cell: RawTextCell,
    meta: {
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('bookingInsuranceRemark', {
    header: () => <span>Insurance Remark</span>,
    cell: RawTextCell,
    meta: {
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('bookingEPDRemark', {
    header: () => <span>EPD Remark</span>,
    cell: RawTextCell,
    meta: {
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
  columnHelper.accessor('bookingCICRemark', {
    header: () => <span>CIC Remark</span>,
    cell: RawTextCell,
    meta: {
      displayLevel: MyOfferDisplayLevel.OFFER,
      displayColor: 'teal',
    },
  }),
]

export const getMyOfferTableColumns = (
  {
    salesUserOptions = [],
    engineerUserOptions = [],
    creatorOptions = [],
    teamOptions = [],
    businessUnitOptions = [],
    PMOptions = [],
    costCenterOptions = [],
    GCKOptions = [],
    closeReasons = [],
    SISICOptions = [],
    teamAssistantOptions = [],
    CPMOptions = [],
    sourcingUserOptions = [],
    vendorOptions = [],
    gcprLineItemPurchaseScenarioOptions = [],
  },
  displayLevel = MyOfferDisplayLevel.OFFER
) => {
  const offerColumns = [
    columnHelper.accessor('status', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values({ ...OfferQuotationStatus, ...OfferBookingStatus, SUPERSEDED: 'Superseded' })
          .concat(closeReasons)
          .map((status) => ({
            label: camelToWords(status),
            value: status,
          })),
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),

    columnHelper.accessor('offerCloseDetailedReason', {
      header: () => <span>Close Detailed Reason</span>,
      meta: {
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('offerStage', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(OfferStage).map((status) => ({
          label: camelToWords(status),
          value: status,
        })),
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),

    ///General Info
    columnHelper.accessor('baseQuotationNumber', {
      header: () => <>Quotation Number</>,
      cell: ({ getValue }) => {
        const baseQuotationNumber = getValue() ?? ''
        const displayQuotationNumber = formatBaseQuotationNumber(baseQuotationNumber)

        return <>{displayQuotationNumber}</>
      },
      meta: {
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('customerNumber', {
      header: () => <>Offer To</>,
      cell: ({ getValue }) => {
        return <>C{getValue()}</>
      },
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('projectName', {
      size: 200,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('businessUnit', {
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: businessUnitOptions,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('team', {
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: teamOptions,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('revision', {
      cell: ({ getValue }) => {
        return <>R{getValue()}</>
      },
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('customer', {
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('countryOfInstallation', {
      header: () => <span>Country of Installation</span>,
      cell: ({ getValue }) => {
        let textValue = getValue()
        if (getValue() === 'hk') {
          textValue = 'Hong Kong'
        } else if (getValue() === 'mo') {
          textValue = 'Macau'
        } else if (getValue() === 'others') {
          textValue = 'CN / Overseas'
        }
        return <TooltipCell text={textValue} />
      },
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: [
          { label: 'Hong Kong', value: 'hk' },
          { label: 'Macau', value: 'mo' },
          { label: 'CN/Overseas', value: 'others' },
        ],
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('salesResponsible', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: salesUserOptions,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('creator', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: creatorOptions,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('updatedAt', {
      header: () => <span>Last Update</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('createdAt', {
      header: () => <span>Create Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),

    columnHelper.accessor('site', {
      meta: {
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('customerReference', {
      header: () => <span>Customer Ref.</span>,
      meta: {
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),

    //Offer Info
    columnHelper.accessor('offerSubmissionDate', {
      header: () => <span>Target Quotation Submission Date</span>,

      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('projectStartDate', {
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('projectEndDate', {
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('projectYear', {
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('projectMonth', {
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('engineers', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: engineerUserOptions,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('manager', {
      header: () => <span>Project Manager</span>,

      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: PMOptions,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('requirePhysicalSignature', {
      header: () => <span>Physical Signature</span>,

      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: [
          { label: 'Required', value: 'required' },
          { label: 'Not Required', value: 'notRequired' },
        ],
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('siemensRoleInTheOffer', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: [
          { label: 'Main Contractor', value: 'mainContract' },
          { label: 'Install & Supplier', value: 'subContract' },
          { label: 'Other', value: 'other' },
        ],
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('endCustomer', {
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('SISIC', {
      cell: ({ getValue }) => {
        const code = getValue()
        const selectedOption = SISICOptions.find((opt) => opt.value === code)
        return <TooltipCell text={selectedOption ? selectedOption?.label : code} />
      },
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: SISICOptions,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('LoANumber', {
      header: () => <span>LoA </span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('currency', {
      size: 200,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(Currency).map((currency) => ({
          label: currency,
          value: currency,
        })),
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('exchangeRate', {
      size: 200,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('executiveSummary', {
      size: 200,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('quotationCompletedDate', {
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('bookingCompletedDate', {
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('quotationCompletedFY', {
      size: 200,
      header: () => <span>Quotation Completed FY</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('bookingCompletedFY', {
      size: 200,
      header: () => <span>Booking Completed FY</span>,

      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),

    //Sub System
    columnHelper.accessor('systemType', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: [
          { label: 'Maintenance', value: 'maintenance' },
          { label: 'Project', value: 'project' },
        ],
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('allSubSystemNewOrRenewal', {
      header: () => <span>New / Renewal</span>,

      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: [
          { label: 'New', value: 'new' },
          { label: 'Renewal', value: 'renewal' },
          {
            label: 'Renewal Of Discontinued',
            value: 'renewalOfDiscontinued',
          },
        ],
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),

    columnHelper.accessor('profitCenters', {
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: costCenterOptions,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('offerQuotationGrossMargin', {
      cell: ({ getValue }) => {
        return <TooltipCell text={`${numberWithCommas(getValue() || 0)}%`} />
      },
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('offerBookingGrossMargin', {
      cell: ({ getValue }) => {
        return <TooltipCell text={`${numberWithCommas(getValue() || 0)}%`} />
      },
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('offerQuotationContractPrice', {
      cell: PriceCell,

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('offerBookingContractPrice', {
      cell: PriceCell,

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),

    columnHelper.accessor('GCKCodes', {
      header: () => <span>GCK</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: GCKOptions,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('equipmentIds', {
      header: () => <span>Equipment IDs</span>,
      cell: RawTextCell,

      meta: {
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),

    //Approval

    columnHelper.accessor('quotationSubmittedDate', {
      size: 200,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('quotationCPM', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: CPMOptions,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('bookingCPM', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: CPMOptions,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('nextApprover', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: creatorOptions,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),

    //Damex
    columnHelper.accessor('damexCreateDate', {
      size: 200,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,

        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('damexRequireSalesToConfirm', {
      size: 200,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,

        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('damexConfirmedBySales', {
      size: 200,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,

        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('damexSigned', {
      size: 200,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,

        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('damexConfirmDate', {
      size: 200,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,

        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('isSOP', {
      header: () => <span>Security of Payment</span>,
      cell: ({ getValue }) => <TooltipCell text={getValue() ? 'Yes' : 'No'} />,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),

    ...commercialChecklistTableColumns,
    ...legalTableColumns,
    ...bookingTableColumns({
      teamAssistantOptions: teamAssistantOptions,
    }),
    columnHelper.accessor('costStructureGCBAU', {
      header: () => <>GC-BAU ($)</>,
      cell: PriceCell,

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('costStructureGCFS', {
      header: () => <>GC-FS ($)</>,
      cell: PriceCell,

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('costStructureGCOther', {
      header: () => <>GC-Other ($)</>,
      cell: PriceCell,

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('costStructureMaterial', {
      header: () => <>Material ($)</>,
      cell: PriceCell,

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('costStructureInternalLabour', {
      header: () => <>Internal Labour ($)</>,
      cell: PriceCell,

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('costStructureSubcontractor', {
      header: () => <>Subcontractor ($)</>,
      cell: PriceCell,

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('costStructureOther', {
      header: () => <>Other ($)</>,
      cell: PriceCell,

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('costStructureOtherExpense', {
      header: () => <>Other Expense ($)</>,
      cell: PriceCell,

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('costStructureMisc', {
      header: () => <>Misc ($)</>,
      cell: PriceCell,

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('costStructureGCBAUPercentage', {
      header: () => <>GC-BAU (%)</>,
      cell: ({ getValue }) => {
        const percentage = getValue() ?? 0

        return <span>{numberWithCommas(percentage.toFixed(2))}%</span>
      },

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('costStructureGCFSPercentage', {
      header: () => <>GC-FS (%)</>,
      cell: ({ getValue }) => {
        const percentage = getValue() ?? 0

        return <span>{numberWithCommas(percentage.toFixed(2))}%</span>
      },

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('costStructureGCOtherPercentage', {
      header: () => <>GC-Other (%)</>,
      cell: ({ getValue }) => {
        const percentage = getValue() ?? 0

        return <span>{numberWithCommas(percentage.toFixed(2))}%</span>
      },

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('costStructureMaterialPercentage', {
      header: () => <>Material (%)</>,
      cell: ({ getValue }) => {
        const percentage = getValue() ?? 0

        return <span>{numberWithCommas(percentage.toFixed(2))}%</span>
      },

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('costStructureInternalLabourPercentage', {
      header: () => <>Internal Labour (%)</>,
      cell: ({ getValue }) => {
        const percentage = getValue() ?? 0

        return <span>{numberWithCommas(percentage.toFixed(2))}%</span>
      },

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('costStructureSubcontractorPercentage', {
      header: () => <>Subcontractor (%)</>,
      cell: ({ getValue }) => {
        const percentage = getValue() ?? 0

        return <span>{numberWithCommas(percentage.toFixed(2))}%</span>
      },

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('costStructureOtherPercentage', {
      header: () => <>Other (%)</>,
      cell: ({ getValue }) => {
        const percentage = getValue() ?? 0

        return <span>{numberWithCommas(percentage.toFixed(2))}%</span>
      },

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('costStructureOtherExpensePercentage', {
      header: () => <>Other Expense (%)</>,
      cell: ({ getValue }) => {
        const percentage = getValue() ?? 0

        return <span>{numberWithCommas(percentage.toFixed(2))}%</span>
      },

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
    columnHelper.accessor('costStructureMiscPercentage', {
      header: () => <>Misc (%)</>,
      cell: ({ getValue }) => {
        const percentage = getValue() ?? 0

        return <span>{numberWithCommas(percentage.toFixed(2))}%</span>
      },

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.OFFER,
        displayColor: 'teal',
      },
    }),
  ]

  const costBudgetColumns = [
    columnHelper.accessor('costBudgetRfqNumber', {
      header: () => <span>RFQ #</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetSubSystemName', {
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetScopeOfSupply', {
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('sourcingDueDate', {
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),

    columnHelper.accessor('procurementResponsible', {
      header: () => <span>Procurement Responsible</span>,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: sourcingUserOptions,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetProcurementCommented', {
      header: () => <span>Procurement Commented</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetNumberOfContractorPosted', {
      header: () => <span># of Contractor Posted</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetNumberOfUpload', {
      header: () => <span># of Contractor Uploaded</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetNumberOfPRSubmission', {
      header: () => <span># of PR Submission</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),

    columnHelper.accessor('costBudgetPM040SourcingSelection', {
      header: () => <span>PM040 Sourcing Selection</span>,
      cell: ({ getValue }) => {
        const value = getValue()
        return <TooltipCell text={value ? value.replace('selected-', '') : value} />
      },
      meta: {
        filterVariant: FilterType.SELECT,

        filterOptions: [
          { label: 'Selected', value: 'selected' },
          { label: 'Not Selected', value: '(-)' },
          { label: 'Reference Only', value: 'reference' },
          ...vendorOptions,
        ],
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetPM040TechnicalSelection', {
      header: () => <span>PM040 Technical Selection</span>,
      cell: ({ getValue }) => {
        const value = getValue()
        return <TooltipCell text={camelToWords(value ? value.replace('selected-', '') : value)} />
      },

      meta: {
        filterVariant: FilterType.SELECT,
        filterOptions: [
          { label: 'Selected', value: 'selected' },
          { label: 'Not Selected', value: '(-)' },
          { label: 'Reference Only', value: 'reference' },

          ...vendorOptions,
        ],
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),

    columnHelper.accessor('costBudgetPM100TechnicalSelection', {
      header: () => <span>PM100 Technical Selection</span>,
      cell: ({ getValue }) => {
        const value = getValue()
        return <TooltipCell text={camelToWords(value ? value.replace('selected-', '') : value)} />
      },

      meta: {
        filterVariant: FilterType.SELECT,
        filterOptions: [
          { label: 'Selected', value: 'selected' },
          { label: 'Not Selected', value: '(-)' },
          { label: 'Reference Only', value: 'reference' },

          ...vendorOptions,
        ],
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetQuoteAmount', {
      header: () => <span>Latest Quote Amount</span>,
      cell: ({ getValue, row: { original } }) => {
        const value = getValue()
        const tooltip = original?.lastUploadVendor ? `Last uploaded by ${original?.lastUploadVendor}` : ''
        return (
          <TooltipCell
            text={formatCurrencyAmount(value ?? 0, original?.costBudgetLatestQuoteCurrency ?? '')}
            tooltip={tooltip}
          />
        )
      },

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetNoticedProcurementOfficer', {
      header: () => <span>Noticed Procurement Officer</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetRequireProcurementOfficer', {
      header: () => <span>Required Procurement Officer</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),

    columnHelper.accessor('costBudgetInternalRemark', {
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetIsActive', {
      header: () => <span>Cost Budget Is Active</span>,
      cell: ({ getValue }) => <TooltipCell text={getValue() === 'yes' ? 'Yes' : 'No'} />,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetType', {
      header: () => <span>Cost Budget Type</span>,
      cell: ({ getValue }) => <TooltipCell text={CostBudgetTypeLabel[getValue()] || getValue() || ''} />,
      meta: {
        filterVariant: FilterType.SELECT,
        filterOptions: Object.entries(CostBudgetTypeLabel).map(([value, label]) => ({ label, value })),
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetLatestQuoteCurrency', {
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(Currency).map((currency) => ({
          label: currency,
          value: currency,
        })),
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),

    columnHelper.accessor('costBudgetLargerThanThreshold', {
      header: () => <span>Cost Budget {'>'}100K</span>,

      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetWorkflowStatus', {
      header: () => <span>Workflow Status</span>,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(SourcingDecisionWorkflowStatus).map((status) => ({
          label: status,
          value: status,
        })),
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetAddendumStatus', {
      header: () => <span>Addendum Status</span>,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(TenderDocumentStatus).map((status) => ({
          label: status,
          value: status,
        })),
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetCTG', {
      header: () => <span>CTG</span>,
      cell: ({ getValue }) => {
        return <TooltipCell text={`${numberWithCommas(getValue() || 0)}`} />
      },

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetCNI', {
      header: () => <span>CNI</span>,
      cell: ({ getValue }) => {
        return <TooltipCell text={`${numberWithCommas(getValue() || 0)}`} />
      },

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
    columnHelper.accessor('costBudgetAppliedSingleSource', {
      header: () => <span>Apply Single Source Procurement ? </span>,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET,
        displayColor: 'blue',
      },
    }),
  ]
  const costBudgetRfqColumns = [
    columnHelper.accessor('costBudgetRfqVendorName', {
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.COST_BUDGET_RFQ,
        displayColor: 'yellow',
      },
    }),
    columnHelper.accessor('costBudgetRfqQuotationUploader', {
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.COST_BUDGET_RFQ,
        displayColor: 'yellow',
      },
    }),
    columnHelper.accessor('costBudgetRfqQuotationAmount', {
      cell: ({ getValue, row: { original } }) => {
        const value = getValue()
        return <TooltipCell text={`$${numberWithCommas(getValue())}`} />
      },
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET_RFQ,
        displayColor: 'yellow',
      },
    }),

    columnHelper.accessor('costBudgetRfqValidDate', {
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET_RFQ,
        displayColor: 'yellow',
      },
    }),
    columnHelper.accessor('costBudgetRfqSubmitDate', {
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET_RFQ,
        displayColor: 'yellow',
      },
    }),

    // columnHelper.accessor('costBudgetRfqCurrency', {
    //   cell: RawTextCell,
    //   meta: {
    //     filterVariant: FilterType.MULTI_SELECT,
    //     filterOptions: Object.values(Currency).map((currency) => ({
    //       label: currency,
    //       value: currency,
    //     })),
    //     displayLevel: MyOfferDisplayLevel.COST_BUDGET_RFQ,
    //     displayColor: 'yellow',
    //   },
    // }),
    columnHelper.accessor('costBudgetRfqPM040SourcingSelected', {
      header: () => <span>PM040 Sourcing</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET_RFQ,
        displayColor: 'yellow',
      },
    }),
    columnHelper.accessor('costBudgetRfqPM040TechnicalSelected', {
      header: () => <span>PM040 Technical</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET_RFQ,
        displayColor: 'yellow',
      },
    }),
    columnHelper.accessor('costBudgetRfqPM100TechnicalSelected', {
      header: () => <span>PM100 Technical</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.COST_BUDGET_RFQ,
        displayColor: 'yellow',
      },
    }),
    columnHelper.accessor('costBudgetRfqSelection', {
      cell: ({ getValue }) => {
        const selection = getValue()
        if (!Array.isArray(selection) || selection.length === 0) {
          return <>(-)</>
        }
        return <TooltipCell text={selection.join(', ')} />
      },

      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: [
          {
            value: 'PM040',
            label: 'PM040',
          },
          {
            value: 'PM040Selected',
            label: 'PM040 Selected',
          },
          {
            value: 'PM0100',
            label: 'PM0100',
          },
          {
            value: 'PM0100Selected',
            label: 'PM0100 Selected',
          },
          {
            value: 'PR',
            label: 'PR',
          },
        ],
        displayLevel: MyOfferDisplayLevel.COST_BUDGET_RFQ,
        displayColor: 'yellow',
      },
    }),
    // columnHelper.accessor('costBudgetRfqTenderDocumentIndex', {
    //   header: () => <span>Tender Doc Version</span>,
    //   cell: ({ getValue }) => <TooltipCell text={getValue() === 0 ? 'Tender' : 'Addendum ' + getValue()} />,

    //   meta: {
    //     filterVariant: FilterType.SELECT_YES_NO,
    //     displayLevel: MyOfferDisplayLevel.COST_BUDGET_RFQ,
    //     displayColor: 'yellow',
    //   },
    // }),
  ]

  const subSystemColumns = [
    columnHelper.accessor('subSystemName', {
      cell: RawTextCell,

      meta: {
        displayLevel: MyOfferDisplayLevel.SUB_SYSTEM,
        displayColor: 'red',
      },
    }),
    columnHelper.accessor('subSystemType', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: [
          { label: 'Maintenance', value: 'maintenance' },
          { label: 'Project', value: 'project' },
        ],
        displayLevel: MyOfferDisplayLevel.SUB_SYSTEM,

        displayColor: 'red',
      },
    }),
    columnHelper.accessor('subSystemNewOrRenewal', {
      header: () => <span>New / Renewal</span>,

      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: [
          { label: 'New', value: 'new' },
          { label: 'Renewal', value: 'renewal' },
          {
            label: 'Renewal Of Discontinued',
            value: 'renewalOfDiscontinued',
          },
        ],
        displayLevel: MyOfferDisplayLevel.SUB_SYSTEM,

        displayColor: 'red',
      },
    }),

    columnHelper.accessor('subSystemProfitCenter', {
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: costCenterOptions,
        displayLevel: MyOfferDisplayLevel.SUB_SYSTEM,

        displayColor: 'red',
      },
    }),
    columnHelper.accessor('subSystemQuotationGrossMargin', {
      cell: ({ getValue }) => {
        return <TooltipCell text={`${numberWithCommas(getValue() || 0)}%`} />
      },
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.SUB_SYSTEM,

        displayColor: 'red',
      },
    }),
    columnHelper.accessor('subSystemBookingGrossMargin', {
      cell: ({ getValue }) => {
        return <TooltipCell text={`${numberWithCommas(getValue() || 0)}%`} />
      },
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.SUB_SYSTEM,

        displayColor: 'red',
      },
    }),
    columnHelper.accessor('subSystemQuotationContractPrice', {
      cell: PriceCell,

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.SUB_SYSTEM,

        displayColor: 'red',
      },
    }),
    columnHelper.accessor('subSystemBookingContractPrice', {
      cell: PriceCell,

      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.SUB_SYSTEM,

        displayColor: 'red',
      },
    }),

    columnHelper.accessor('subSystemGCKCode', {
      header: () => <span>Sub System GCK</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: GCKOptions,
        displayLevel: MyOfferDisplayLevel.SUB_SYSTEM,

        displayColor: 'red',
      },
    }),
    columnHelper.accessor('subSystemEquipmentIds', {
      header: () => <span>Sub System Equipment IDs</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.SUB_SYSTEM,

        displayColor: 'red',
      },
    }),
    columnHelper.accessor('subSystemExistingWBSNumber', {
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.SUB_SYSTEM,
        displayColor: 'red',
      },
    }),
    columnHelper.accessor('subSystemUpcomingQuotationNumber', {
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.SUB_SYSTEM,
        displayColor: 'red',
      },
    }),
    columnHelper.accessor('subSystemPreviousQuotationNumber', {
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.SUB_SYSTEM,
        displayColor: 'red',
      },
    }),
    columnHelper.accessor('subSystemUpcomingQuotationStatus', {
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values({ ...OfferQuotationStatus, ...OfferBookingStatus }).map((status) => {
          return {
            label: camelToWords(status),
            value: status,
          }
        }),
        displayLevel: MyOfferDisplayLevel.SUB_SYSTEM,
        displayColor: 'red',
      },
    }),
    columnHelper.accessor('maintenanceContractStatus', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(MaintenanceSummaryStatus).map((status) => {
          return {
            label: camelToWords(status),
            value: status,
          }
        }),
        displayLevel: MyOfferDisplayLevel.SUB_SYSTEM,
        displayColor: 'red',
      },
    }),
  ]

  // return [...offerColumns, ...subSystemColumns, ...costBudgetColumns, ...costBudgetRfqColumns]

  const gcprColumns = [
    columnHelper.accessor('gcprPRNumber', {
      header: () => <span>PR Number</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprRaiser', {
      header: () => <span>Raiser</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprRequestor', {
      header: () => <span>Requestor</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprRequestDate', {
      header: () => <span>Request Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprTotalAmount', {
      header: () => <span>Total Amount</span>,
      cell: PriceCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprDescriptionOfProduct', {
      header: () => <span>Description of Product / Service</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprExpectedDeliveryDate', {
      header: () => <span>Expected Delivery Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprDeliveryInstructions', {
      header: () => <span>Delivery Instructions</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprStatus', {
      header: () => <span>Status</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(GCPRStatus).map((status) => ({ value: status, label: status })),
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprApprovalDueDate', {
      header: () => <span>Approval Due Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprSONumber', {
      header: () => <span>SO# / SAP PR#</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprNextApprover', {
      header: () => <span>Next Approver</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLicenseRequired', {
      header: () => <span>License</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprApprovedDate', {
      header: () => <span>Approved Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprSubmittedDate', {
      header: () => <span>Submitted Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLastApprovalDate', {
      header: () => <span>Last Approval Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprPONumber', {
      header: () => <span>PO #</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLicenseType', {
      header: () => <span>License Type</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLicenseSiteAddress', {
      header: () => <span>Site Address</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLicenseEmailAddress', {
      header: () => <span>Email Address</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLicenseCsid', {
      header: () => <span>CSID</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLicenseExistingDongleNumber', {
      header: () => <span>Existing Dongle Number</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLicenseFileAttached', {
      header: () => <span>License File Attached</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLicensingManagerRequired', {
      header: () => <span>Licensing Manager Required</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.GCPR,
        displayColor: 'purple',
      },
    }),
  ]

  const gcprLineItemColumns = [
    columnHelper.accessor('gcprLineItemModel', {
      header: () => <span>Model</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLineItemDescription', {
      header: () => <span>Description</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLineItemSAPMaterialNumber', {
      header: () => <span>SAP Material Number</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLineItemQty', {
      header: () => <span>Qty</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.GCPR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLineItemUnit', {
      header: () => <span>Unit</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLineItemUnitCost', {
      header: () => <span>Unit Cost (HKD)</span>,
      cell: PriceCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.GCPR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLineItemTotalCost', {
      header: () => <span>Total Cost (HKD)</span>,
      cell: PriceCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.GCPR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLineItemPurchaseScenario', {
      header: () => <span>Purchase Scenario</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: gcprLineItemPurchaseScenarioOptions,
        displayLevel: MyOfferDisplayLevel.GCPR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLineItemSubSystem', {
      header: () => <span>Sub System</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLineItemVendor', {
      header: () => <span>Vendor</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLineItemPONumber', {
      header: () => <span>PO#</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLineItemCSID', {
      header: () => <span>CSID</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.GCPR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLineItemZUROA', {
      header: () => <span>ZUROA</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.GCPR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLineItemChargeType', {
      header: () => <span>Charge Type</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLineItemBuildingX', {
      header: () => <span>Building X</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.GCPR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLineItemStartDate', {
      header: () => <span>Start Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.GCPR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLineItemSubscriptionId', {
      header: () => <span>Subscription ID</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.GCPR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLineItemTrialEnd', {
      header: () => <span>Trial End</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.GCPR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
    columnHelper.accessor('gcprLineItemContractEnd', {
      header: () => <span>Contract End</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.GCPR_LINE_ITEM,
        displayColor: 'purple',
      },
    }),
  ]

  const prColumns = [
    columnHelper.accessor('prVendor', {
      header: () => <span>Vendor</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prPRNumber', {
      header: () => <span>PR Number</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prRaiser', {
      header: () => <span>Raiser</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prRequestor', {
      header: () => <span>Requestor</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prRequestDate', {
      header: () => <span>Request Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prTotalAmount', {
      header: () => <span>Total Amount</span>,
      cell: PriceCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prDescriptionOfProduct', {
      header: () => <span>Description of Product / Service</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prExpectedDeliveryDate', {
      header: () => <span>Expected Delivery Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prDeliveryInstructions', {
      header: () => <span>Delivery Instructions</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prStatus', {
      header: () => <span>Status</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(PRStatus).map((status) => ({ value: status, label: status })),
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prApprovalDueDate', {
      header: () => <span>Approval Due Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prNextApprover', {
      header: () => <span>Next Approver</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLicenseRequired', {
      header: () => <span>License Required</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prApprovedDate', {
      header: () => <span>Approved Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prSubmittedDate', {
      header: () => <span>Submitted Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLastApprovalDate', {
      header: () => <span>Last Approval Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prPONumber', {
      header: () => <span>PO #</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prSecurityOfPayment', {
      header: () => <span>Security Of Payment</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prSubcontractorAgreementRequired', {
      header: () => <span>Subcontractor Agreement Required</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prSubcontractorAgreementFileAttached', {
      header: () => <span>Subcontractor Agreement Attached</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prInternalDocumentAttached', {
      header: () => <span>Internal Document Attached</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prSAPNumber', {
      header: () => <span>SAP Number</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prCurrency', {
      header: () => <span>Currency</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(Currency).map((currency) => ({ value: currency, label: currency })),
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prRequiredWorkInstructions', {
      header: () => <span>Required Work Instructions</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prWorkInstructionFileAttached', {
      header: () => <span>Work Instruction File Attached</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
        displayLevel: MyOfferDisplayLevel.PR,
        displayColor: 'pink',
      },
    }),
  ]

  const prLineItemColumns = [
    columnHelper.accessor('prLineItemVendor', {
      header: () => <span>Vendor</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemSubSystemName', {
      header: () => <span>Sub System</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemScopeOfWork', {
      header: () => <span>Scope Of Work</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemProductDescription', {
      header: () => <span>Product Description</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemDescription', {
      header: () => <span>Description</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemBrand', {
      header: () => <span>Brand</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemModelNumber', {
      header: () => <span>Model Number</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemSAPMaterialNumber', {
      header: () => <span>SAP Material Number</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemQuantity', {
      header: () => <span>Quantity</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemUnit', {
      header: () => <span>Unit</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: [
          ...new Set([
            ...Object.values(OfferQuotationDetailInstallAndSupplyUnit),
            ...Object.values(OfferQuotationDetailMaintenanceUnit),
          ]),
        ].map((value) => ({ value, label: value })),
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemUnitRate', {
      header: () => <span>Unit Rate</span>,
      cell: PriceCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemDiscount', {
      header: () => <span>Discount (%)</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemTotalPrice', {
      header: () => <span>Total Price</span>,
      cell: PriceCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemCountry', {
      header: () => <span>Country</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemAL', {
      header: () => <span>AL</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemECCN', {
      header: () => <span>ECCN</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemHSCode', {
      header: () => <span>HS Code</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemStartDate', {
      header: () => <span>Start Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemEndDate', {
      header: () => <span>End Date</span>,
      cell: DateCell,
      meta: {
        filterVariant: FilterType.DATE,
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemSiteLocation', {
      header: () => <span>Site Location</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemNumberOfWorker', {
      header: () => <span>No. Of Worker</span>,
      cell: RawTextCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemSystem', {
      header: () => <span>System</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemCategory', {
      header: () => <span>Category</span>,
      cell: RawTextCell,
      meta: {
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
    columnHelper.accessor('prLineItemLumpSumDiscount', {
      header: () => <span>Lump Sum Discount</span>,
      cell: PriceCell,
      meta: {
        filterVariant: FilterType.RANGE,
        displayLevel: MyOfferDisplayLevel.PR_LINE_ITEM,
        displayColor: 'pink',
      },
    }),
  ]

  switch (displayLevel) {
    case MyOfferDisplayLevel.OFFER:
      return offerColumns
    case MyOfferDisplayLevel.SUB_SYSTEM:
      return [...offerColumns, ...subSystemColumns]
    case MyOfferDisplayLevel.COST_BUDGET:
      return [...offerColumns, ...costBudgetColumns]
    case MyOfferDisplayLevel.COST_BUDGET_RFQ:
      return [...offerColumns, ...costBudgetColumns, ...costBudgetRfqColumns]
    case MyOfferDisplayLevel.GCPR:
      return [...offerColumns, ...gcprColumns]
    case MyOfferDisplayLevel.GCPR_LINE_ITEM:
      return [...offerColumns, ...gcprLineItemColumns]
    case MyOfferDisplayLevel.PR:
      return [...offerColumns, ...costBudgetColumns, ...prColumns]
    case MyOfferDisplayLevel.PR_LINE_ITEM:
      return [...offerColumns, ...costBudgetColumns, ...prColumns, ...prLineItemColumns]
    default:
      return offerColumns
  }
}

export const myOfferSubSystemColumnAccessors = [
  'subSystemName',
  'subSystemType',
  'subSystemNewOrRenewal',
  'subSystemProfitCenter',
  'subSystemQuotationGrossMargin',
  'subSystemBookingGrossMargin',
  'subSystemQuotationContractPrice',
  'subSystemBookingContractPrice',
  'subSystemGCKCode',
  'subSystemEquipmentIds',
  'subSystemExistingWBSNumber',
  'subSystemUpcomingQuotationNumber',
  'subSystemPreviousQuotationNumber',
  'subSystemUpcomingQuotationStatus',
  'maintenanceContractStatus',
]

export const myOfferCostBudgetColumnAccessors = [
  'costBudgetRfqNumber',
  'costBudgetSubSystemName',
  'costBudgetScopeOfSupply',
  'sourcingDueDate',
  'procurementResponsible',
  'costBudgetProcurementCommented',
  'costBudgetNumberOfContractorPosted',
  'costBudgetNumberOfUpload',
  'costBudgetPM040SourcingSelection',
  'costBudgetPM040TechnicalSelection',
  'costBudgetPM100TechnicalSelection',
  'costBudgetQuoteAmount',
  'costBudgetNoticedProcurementOfficer',
  'costBudgetRequireProcurementOfficer',
  'costBudgetInternalRemark',
  'costBudgetLatestQuoteCurrency',
  'costBudgetLargerThanThreshold',
  'costBudgetWorkflowStatus',
  'costBudgetAddendumStatus',
  'costBudgetCTG',
  'costBudgetCNI',
  'costBudgetAppliedSingleSource',
]

export const myOfferCostBudgetRfqColumnAccessors = [
  'costBudgetRfqVendorName',
  'costBudgetRfqQuotationUploader',
  'costBudgetRfqQuotationAmount',
  'costBudgetRfqValidDate',
  'costBudgetRfqSubmitDate',
  'costBudgetRfqCurrency',
  'costBudgetRfqPM040SourcingSelected',
  'costBudgetRfqPM040TechnicalSelected',
  'costBudgetRfqPM100TechnicalSelected',
  'costBudgetRfqSelection',
  'costBudgetRfqTenderDocumentIndex',
]

export const myOfferGCPRColumnAccessors = [
  'gcprPRNumber',
  'gcprRaiser',
  'gcprRequestor',
  'gcprRequestDate',
  'gcprTotalAmount',
  'gcprDescriptionOfProduct',
  'gcprExpectedDeliveryDate',
  'gcprDeliveryInstructions',
  'gcprStatus',
  'gcprApprovalDueDate',
  'gcprSONumber',
  'gcprNextApprover',
  'gcprLicenseRequired',
  'gcprApprovedDate',
  'gcprSubmittedDate',
  'gcprLastApprovalDate',
  'gcprPONumber',
  'gcprLicenseType',
  'gcprLicenseSiteAddress',
  'gcprLicenseEmailAddress',
  'gcprLicenseCsid',
  'gcprLicenseExistingDongleNumber',
  'gcprLicenseFileAttached',
  'gcprLicensingManagerRequired',
]

export const myOfferGCPRLineItemColumnAccessors = [
  'gcprLineItemModel',
  'gcprLineItemDescription',
  'gcprLineItemSAPMaterialNumber',
  'gcprLineItemQty',
  'gcprLineItemUnit',
  'gcprLineItemUnitCost',
  'gcprLineItemTotalCost',
  'gcprLineItemPurchaseScenario',
  'gcprLineItemSubSystem',
  'gcprLineItemVendor',
  'gcprLineItemPONumber',
  'gcprLineItemCSID',
  'gcprLineItemZUROA',
  'gcprLineItemChargeType',
  'gcprLineItemBuildingX',
  'gcprLineItemStartDate',
  'gcprLineItemSubscriptionId',
  'gcprLineItemTrialEnd',
  'gcprLineItemContractEnd',
]

export const myOfferPRColumnAccessors = [
  'prVendor',
  'prPRNumber',
  'prRaiser',
  'prRequestor',
  'prRequestDate',
  'prTotalAmount',
  'prDescriptionOfProduct',
  'prExpectedDeliveryDate',
  'prDeliveryInstructions',
  'prStatus',
  'prApprovalDueDate',
  'prNextApprover',
  'prLicenseRequired',
  'prApprovedDate',
  'prSubmittedDate',
  'prLastApprovalDate',
  'prPONumber',
  'prSecurityOfPayment',
  'prSubcontractorAgreementRequired',
  'prSubcontractorAgreementFileAttached',
  'prInternalDocumentAttached',
  'prSAPNumber',
  'prCurrency',
  'prRequiredWorkInstructions',
  'prWorkInstructionFileAttached',
]

export const myOfferPRLineItemColumnAccessors = [
  'prLineItemVendor',
  'prLineItemSubSystemName',
  'prLineItemScopeOfWork',
  'prLineItemProductDescription',
  'prLineItemDescription',
  'prLineItemBrand',
  'prLineItemModelNumber',
  'prLineItemSAPMaterialNumber',
  'prLineItemQuantity',
  'prLineItemUnit',
  'prLineItemUnitRate',
  'prLineItemDiscount',
  'prLineItemTotalPrice',
  'prLineItemCountry',
  'prLineItemAL',
  'prLineItemECCN',
  'prLineItemHSCode',
  'prLineItemStartDate',
  'prLineItemEndDate',
  'prLineItemSiteLocation',
  'prLineItemNumberOfWorker',
  'prLineItemSystem',
  'prLineItemCategory',
  'prLineItemLumpSumDiscount',
]
